import { useEffect, useRef } from "react";
import { AuditEvents, detectOS } from "./auditLogger";

/**
 * Windows screen capture shortcuts watched by Capture Detection.
 *
 *   Win + Shift + S   Snipping Tool - region / window / full screen snip
 *   Win + Shift + R   Snipping Tool - screen recording (Windows 11)
 *   Win + G           Xbox Game Bar (capture widget)
 *   Win + Alt + R     Xbox Game Bar - start / stop recording
 *
 * Matched on the physical `code` OR the legacy `keyCode` (the Windows virtual
 * key, which is what the OS hotkey itself is bound to). `key` is not reliable
 * here: with Shift held it is "S", and with Win held some builds report "".
 *
 * Plain Win + R (Run dialog) and Win + S (Search) are deliberately NOT matched:
 * the R combos require Shift or Alt, and S requires Shift.
 */
const WINDOWS_CAPTURE_COMBOS = [
  { label: "Win+Shift+S (Snipping Tool)", code: "KeyS", keyCode: 83, shift: true, alt: false },
  { label: "Win+Shift+R (Snipping Tool recording)", code: "KeyR", keyCode: 82, shift: true, alt: false },
  { label: "Win+G (Xbox Game Bar)", code: "KeyG", keyCode: 71, shift: null, alt: false },
  { label: "Win+Alt+R (Xbox Game Bar recording)", code: "KeyR", keyCode: 82, shift: null, alt: true },
];

const legacyCode = (e) => e.keyCode || e.which;

const isMetaKeyEvent = (e) =>
  e.key === "Meta" ||
  e.key === "OS" ||
  e.code === "MetaLeft" ||
  e.code === "MetaRight" ||
  e.code === "OSLeft" ||
  e.code === "OSRight" ||
  legacyCode(e) === 91 ||
  legacyCode(e) === 92;

const isShiftKeyEvent = (e) => e.key === "Shift" || legacyCode(e) === 16;

const isAltKeyEvent = (e) => e.key === "Alt" || legacyCode(e) === 18;

/**
 * Keys Windows injects on its own while a Win hotkey is handled (the "menu
 * mask" key that stops the Start menu opening on Win key-up, and similar).
 * They carry no real key and must not count as "the reader pressed another
 * key", or the fallbacks below would be switched off by the very shortcut
 * they are meant to catch.
 */
const isInjectedKeyEvent = (e) => {
  const legacy = legacyCode(e);
  return (
    e.key === "Unidentified" ||
    e.key === "" ||
    legacy === 0 ||
    legacy === 255 ||
    legacy === 232 || // VK 0xE8 - unassigned, used as the mask key
    legacy === 229 // IME process key
  );
};

/**
 * Keyboard Shortcut Protection.
 *
 * Two modes:
 *
 *   captureOnly = false (full guard)
 *       Intercepts application-level shortcuts that could be used to
 *       exfiltrate the document (print, save, copy/cut, select-all,
 *       view-source, devtools) AND watches the screen capture shortcuts.
 *
 *   captureOnly = true (RShield)
 *       Watches ONLY the screen capture shortcuts, and blocks nothing. RShield
 *       has no option to disable printing or copying - those are governed by
 *       the document's own permissions - so it must not intercept them.
 *
 * Capture shortcuts watched:
 *   macOS    Cmd+Shift+3 / 4 / 5 / 6
 *   Windows  Print Screen, Win+Shift+S, Win+Shift+R, Win+G, Win+Alt+R
 *
 * NOTE: browsers do not guarantee blocking every shortcut. Some browser-reserved
 * shortcuts (e.g., F12 or menu actions) may still work depending on browser
 * policies and user settings. OS level screen capture cannot be blocked at all;
 * the shortcut is detected and reported, never prevented.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {boolean} [opts.captureOnly]  detect capture keys only, block nothing
 * @param {(auditEvent: string) => void} [opts.onBlocked]
 */
export function useKeyboardGuard({ enabled, captureOnly = false, onBlocked } = {}) {
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;

  useEffect(() => {
    if (!enabled) return undefined;

    const report = (auditEvent) => {
      const cb = onBlockedRef.current;
      if (typeof cb === "function") cb(auditEvent);
    };

    /**
     * macOS screen capture combos: Cmd+Shift+3 (full screen), 4 (region),
     * 5 (capture toolbar), 6 (Touch Bar).
     *
     * `event.key` CANNOT be used for these. Holding Shift changes the printable
     * character, so on a US layout Cmd+Shift+3 arrives as key "#", not "3" -
     * and on other layouts it is different again. `code` ("Digit3") and the
     * legacy `keyCode` (51) are both physical-key based and unaffected by Shift
     * or by the keyboard layout, so they are what we match on. The shifted
     * symbols are kept only as a last-resort fallback for browsers that report
     * neither.
     */
    const isMacCaptureCombo = (e) => {
      if (!e.metaKey || !e.shiftKey) return false;
      const digits = ["3", "4", "5", "6"];
      if (e.code && digits.some((d) => e.code === "Digit" + d)) return true;
      const legacy = e.keyCode || e.which;
      if (legacy >= 51 && legacy <= 54) return true;
      // Fallback: the shifted characters produced by those keys on a US layout.
      return ["#", "$", "%", "^"].indexOf(e.key) > -1;
    };

    /**
     * Diagnostic for the macOS combos.
     *
     * macOS may consume Cmd+Shift+3/4/5 in the window server and never deliver
     * it to the browser, in which case no web page can detect it - the event
     * simply never arrives. This logs every Cmd/Meta+Shift key the page DOES
     * receive, so the two cases can be told apart in seconds:
     *
     *   press Cmd+Shift+3 with the console open
     *     -> a "[DocumentProtection][keys]" line appears  = the event arrives,
     *        and if no capture was reported the matching is at fault
     *     -> nothing appears at all                       = macOS swallowed it,
     *        and keyboard detection is impossible for that shortcut
     */
    const logMetaShift = (e, phase) => {
      if (!e.metaKey || !e.shiftKey) return;
      // eslint-disable-next-line no-console
      console.log(
        "[DocumentProtection][keys] " + phase +
        " key=" + JSON.stringify(e.key) +
        " code=" + JSON.stringify(e.code) +
        " keyCode=" + (e.keyCode || e.which) +
        " meta=" + e.metaKey + " shift=" + e.shiftKey +
        " -> capture combo: " + isMacCaptureCombo(e)
      );
    };

    /**
     * Windows screen capture combos (see WINDOWS_CAPTURE_COMBOS).
     *
     * Windows registers these as shell hotkeys, so the letter's KEYDOWN is
     * normally swallowed by the OS and never reaches the page. What usually
     * does arrive is:
     *   - keydown of the Win key (and Shift / Alt), and
     *   - keyup of the letter, if it is released before the Snipping Tool /
     *     Game Bar overlay takes focus.
     * So the combo is matched on both keydown and keyup, and the Win key state
     * is tracked here as well because `metaKey` can be stale on the keyup.
     * When the letter's keyup never arrives either, the window blur fallback
     * below catches Win + Shift + S / R.
     *
     * Windows only: on macOS Cmd+Shift+R is a hard reload and Cmd+G is Find
     * Next, which must never count as a capture attempt.
     */
    const isWindows = detectOS() === "Windows";
    const HELD_MAX_MS = 5000;
    // How long a Win + Shift press can sit with its key-ups missing before the
    // "lost key-up" fallback stops trusting it (the reader may spend a while in
    // the snipping overlay before coming back).
    const LOST_KEYUP_MAX_MS = 5 * 60 * 1000;

    /**
     * Windows diagnostics. Every event that matters for the Win shortcuts is
     * logged with a "[DocumentProtection][win]" prefix, so a test run shows at
     * a glance which of the signals the browser actually delivered.
     */
    const isMac = detectOS() === "macOS";
    const winLog = (text) => {
      if (!isWindows && !isMac) return;
      // eslint-disable-next-line no-console
      console.log("[DocumentProtection][" + (isWindows ? "win" : "mac") + "] " + text);
    };
    const describeKey = (e) =>
      e.type +
      " key=" + JSON.stringify(e.key) +
      " code=" + JSON.stringify(e.code) +
      " keyCode=" + legacyCode(e) +
      " meta=" + !!e.metaKey +
      " shift=" + !!e.shiftKey +
      " alt=" + !!e.altKey +
      " ctrl=" + !!e.ctrlKey;
    const describeMods = () =>
      "tracked{meta=" + mods.meta +
      " shift=" + mods.shift +
      " alt=" + mods.alt +
      " otherKey=" + mods.otherKey + "}";
    // How long after a bare Win + Shift chord is released a window blur is
    // still attributed to it. Win + Shift + R starts the Snipping Tool
    // recorder, which only takes focus once the keys are already up - and a
    // cold start of the app can take a couple of seconds.
    const CHORD_BLUR_MS = 4000;
    const mods = {
      meta: false,
      metaAt: 0,
      shift: false,
      shiftAt: 0,
      alt: false,
      otherKey: false,
      // Shift was down at some point while Win was held (this Win press).
      sawShift: false,
      // Time a Win + Shift chord was released with no visible key in between.
      chordReleasedAt: 0,
    };
    const resetMods = () => {
      mods.meta = false;
      mods.metaAt = 0;
      mods.shift = false;
      mods.shiftAt = 0;
      mods.alt = false;
      mods.otherKey = false;
      mods.sawShift = false;
      mods.chordReleasedAt = 0;
    };
    const metaHeld = (e) =>
      !!(e && e.metaKey) || (mods.meta && Date.now() - mods.metaAt < HELD_MAX_MS);

    /** Keeps the modifier state in step with the events the page receives. */
    const trackModifiers = (e, isDown) => {
      if (!isWindows) return;
      if (isMetaKeyEvent(e)) {
        if (isDown && !mods.meta) {
          // A fresh Win press starts a new chord.
          mods.otherKey = false;
          mods.alt = !!e.altKey;
          mods.sawShift = !!e.shiftKey;
          mods.chordReleasedAt = 0;
        }
        if (!isDown && mods.meta) {
          // Win released. If Shift was part of the chord and no key showed up
          // in between, the letter was swallowed by a Win + Shift hotkey
          // (Win + Shift + S / R). Remember it for the blur that follows.
          mods.chordReleasedAt =
            mods.sawShift && !mods.otherKey && !mods.alt ? Date.now() : 0;
          mods.sawShift = false;
        }
        mods.meta = isDown;
        mods.metaAt = isDown ? mods.metaAt || Date.now() : 0;
        return;
      }
      if (isShiftKeyEvent(e)) {
        mods.shift = isDown;
        mods.shiftAt = isDown ? mods.shiftAt || Date.now() : 0;
        if (isDown && mods.meta) mods.sawShift = true;
        return;
      }
      if (isAltKeyEvent(e)) {
        mods.alt = isDown || (mods.meta && mods.alt);
        return;
      }
      // Modifier key-ups can be lost when focus moves away; the modifier
      // flags on any later event are the source of truth.
      if (!e.metaKey) {
        mods.meta = false;
        mods.metaAt = 0;
      }
      if (!e.shiftKey) {
        mods.shift = false;
        mods.shiftAt = 0;
      }
      if (!e.altKey) mods.alt = false;
    };

    /** @returns {string} the matched combo label, or "" */
    const matchWindowsCaptureCombo = (e) => {
      if (!isWindows) return "";
      if (!metaHeld(e)) return "";
      if (e.ctrlKey) return "";
      const legacy = legacyCode(e);
      const shift = !!(e.shiftKey || mods.shift);
      const alt = !!(e.altKey || mods.alt);
      for (let i = 0; i < WINDOWS_CAPTURE_COMBOS.length; i++) {
        const c = WINDOWS_CAPTURE_COMBOS[i];
        const keyMatch = e.code === c.code || legacy === c.keyCode;
        if (!keyMatch) continue;
        if (c.alt !== alt) continue;
        if (c.shift !== null && c.shift !== shift) continue;
        return c.label;
      }
      return "";
    };

    // One physical press can surface as both keydown and keyup - and on macOS
    // the system sometimes delivers only one of them - so both are watched and
    // de-duplicated over a short window.
    let lastCaptureReport = 0;
    const reportCaptureOnce = (source) => {
      const now = Date.now();
      if (now - lastCaptureReport < 700) return;
      lastCaptureReport = now;
      // eslint-disable-next-line no-console
      console.log("[DocumentProtection] Screen capture shortcut detected (" + source + ")");
      report(AuditEvents.SCREENSHOT_ATTEMPT);
    };

    /** Shared by keydown / keyup. Returns true when a capture was reported. */
    const checkCaptureCombos = (e, phase) => {
      if (isMacCaptureCombo(e)) {
        reportCaptureOnce("macOS Cmd+Shift, " + phase);
        return true;
      }
      const winCombo = matchWindowsCaptureCombo(e);
      if (winCombo) {
        reportCaptureOnce(winCombo + ", " + phase);
        return true;
      }
      return false;
    };

    /**
     * macOS fallback - Cmd + Shift + 3 / 4 / 5 (and the Ctrl clipboard
     * variants).
     *
     * macOS handles these system shortcuts before the browser: Safari (and
     * Chrome) never receive the digit, not even its key-up. What the page does
     * receive is Cmd and Shift going down and coming back up. So a Cmd + Shift
     * chord that is released with no other key seen in between is treated as
     * a screenshot shortcut:
     *
     *   Cmd+Shift+3  instant capture - no focus change at all
     *   Cmd+Shift+4  crosshair       - usually no focus change
     *   Cmd+Shift+5  capture toolbar - the window loses focus while the keys
     *                are still held, so their key-ups never come back; the
     *                blur / next pointer event stands in for the release
     *
     * A chord followed within MAC_SETTLE_MS by the page becoming hidden was a
     * tab / window shortcut (e.g. Cmd+Shift+] or Cmd+Shift+T) and is ignored.
     * Any key the page does see during the chord (Cmd+Shift+Z, arrows, ...)
     * cancels it. Known limit: a bare Cmd+Shift press-and-release, or
     * Cmd+Shift+Tab (reverse app switch), looks the same and is reported too.
     */
    const MAC_SETTLE_MS = 800;
    const mac = {
      cmd: false,
      shift: false,
      chord: false,
      other: false,
      settleTimer: null,
      hiddenDuringSettle: false,
    };
    const isMacModifierEvent = (e) =>
      isMetaKeyEvent(e) ||
      e.code === "MetaRight" ||
      isShiftKeyEvent(e) ||
      isAltKeyEvent(e) ||
      e.key === "Control" ||
      legacyCode(e) === 17 ||
      e.key === "CapsLock" ||
      e.key === "Fn";
    const macClearSettle = () => {
      if (mac.settleTimer) clearTimeout(mac.settleTimer);
      mac.settleTimer = null;
      mac.hiddenDuringSettle = false;
    };
    const macResetChord = () => {
      mac.cmd = false;
      mac.shift = false;
      mac.chord = false;
      mac.other = false;
    };
    /** The chord is over - decide once the page has had time to hide. */
    const macChordReleased = (source) => {
      const wasCapture = mac.chord && !mac.other;
      winLog(
        "Cmd+Shift chord released via " + source +
        " (chord=" + mac.chord + " otherKey=" + mac.other + ")"
      );
      macResetChord();
      if (!wasCapture) return;
      macClearSettle();
      mac.settleTimer = setTimeout(() => {
        mac.settleTimer = null;
        if (mac.hiddenDuringSettle) {
          mac.hiddenDuringSettle = false;
          winLog("Cmd+Shift chord ignored - page was hidden (tab/window shortcut)");
          return;
        }
        reportCaptureOnce(
          "macOS Cmd+Shift+3/4/5 (digit taken by the system), " + source
        );
      }, MAC_SETTLE_MS);
    };
    const macKeyDown = (e) => {
      if (!isMac || e.repeat) return;
      if (isMacModifierEvent(e)) {
        const wasIdle = !mac.cmd && !mac.shift;
        if (wasIdle) {
          mac.chord = false;
          mac.other = false;
        }
        if (isMetaKeyEvent(e) || e.code === "MetaRight") mac.cmd = true;
        if (isShiftKeyEvent(e)) mac.shift = true;
        // A modifier pressed before tracking started still shows in the flags.
        if (e.metaKey) mac.cmd = true;
        if (e.shiftKey) mac.shift = true;
        if (mac.cmd && mac.shift) mac.chord = true;
        return;
      }
      if (isInjectedKeyEvent(e)) return;
      if (mac.cmd || mac.shift || e.metaKey) mac.other = true;
    };
    const macKeyUp = (e) => {
      if (!isMac) return;
      if (!isMacModifierEvent(e)) {
        if (mac.cmd || mac.shift) mac.other = true;
        return;
      }
      if (isMetaKeyEvent(e) || e.code === "MetaRight") mac.cmd = false;
      if (isShiftKeyEvent(e)) mac.shift = false;
      // Trust the flags for whatever is still held.
      mac.cmd = mac.cmd && !!e.metaKey;
      mac.shift = mac.shift && !!e.shiftKey;
      if (!mac.cmd && !mac.shift && (mac.chord || mac.other)) {
        macChordReleased("key-up");
      }
    };
    const macOnBlur = () => {
      if (!isMac) return;
      // Cmd+Shift+5: the toolbar takes focus while the keys are still down,
      // so their key-ups go to the toolbar. Treat the blur as the release.
      if ((mac.cmd || mac.shift) && mac.chord) macChordReleased("window blur");
      else macResetChord();
    };
    const macOnPointer = (e) => {
      if (!isMac) return;
      if (!(mac.cmd || mac.shift)) return;
      if (e.metaKey || e.shiftKey) return;
      // Key-ups were lost (delivered elsewhere) - the keys are up now.
      if (mac.chord) macChordReleased(e.type + " (key-ups lost)");
      else macResetChord();
    };
    const macOnVisibility = () => {
      if (!isMac) return;
      if (document.visibilityState === "hidden") {
        if (mac.settleTimer) mac.hiddenDuringSettle = true;
        macResetChord();
      }
    };

    const handler = (e) => {
      logMetaShift(e, "keydown");
      if (isWindows && !e.repeat && (e.metaKey || mods.meta || isMetaKeyEvent(e))) {
        winLog(describeKey(e) + " " + describeMods());
      } else if (isMac && !e.repeat && (e.metaKey || isMetaKeyEvent(e) || isShiftKeyEvent(e))) {
        winLog(describeKey(e) + " -> capture combo: " + isMacCaptureCombo(e));
      }
      macKeyDown(e);
      // Any real key after a released Win + Shift chord means the reader is
      // simply typing again - the chord did not open anything.
      if (
        isWindows &&
        mods.chordReleasedAt &&
        !isMetaKeyEvent(e) &&
        !isInjectedKeyEvent(e)
      ) {
        mods.chordReleasedAt = 0;
      }
      trackModifiers(e, true);
      const captured = checkCaptureCombos(e, "keydown");
      // A real non-modifier key that reached the page while Win was held means
      // the OS did not swallow it, so the fallbacks must not fire for it.
      if (
        isWindows &&
        mods.meta &&
        !isMetaKeyEvent(e) &&
        !isShiftKeyEvent(e) &&
        !isAltKeyEvent(e) &&
        !isInjectedKeyEvent(e)
      ) {
        mods.otherKey = true;
      }

      // RShield: capture shortcuts are detected above; nothing else is
      // intercepted or reported.
      if (captureOnly || captured) return undefined;

      const key = (e.key || "").toLowerCase();
      const code = e.keyCode || e.which;
      const ctrlOrMeta = e.ctrlKey || e.metaKey;

      // DevTools / view-source (no modifier combos handled first).
      if (key === "f12" || code === 123) {
        e.preventDefault();
        report(AuditEvents.DEVTOOLS_ATTEMPT);
        return false;
      }

      if (ctrlOrMeta && e.shiftKey) {
        // Ctrl/Cmd + Shift + I / J / C  -> devtools / console / inspect
        if (key === "i" || code === 73) {
          e.preventDefault();
          report(AuditEvents.DEVTOOLS_ATTEMPT);
          return false;
        }
        if (key === "j" || code === 74) {
          e.preventDefault();
          report(AuditEvents.DEVTOOLS_ATTEMPT);
          return false;
        }
        if (key === "c" || code === 67) {
          e.preventDefault();
          report(AuditEvents.DEVTOOLS_ATTEMPT);
          return false;
        }
      }

      if (ctrlOrMeta && !e.shiftKey && !e.altKey) {
        switch (key) {
          case "p": // Print
            e.preventDefault();
            report(AuditEvents.PRINT_ATTEMPT);
            return false;
          case "s": // Save
            e.preventDefault();
            report(AuditEvents.SAVE_ATTEMPT);
            return false;
          case "a": // Select all
            e.preventDefault();
            report(AuditEvents.SELECT_ALL_ATTEMPT);
            return false;
          case "c": // Copy
            e.preventDefault();
            report(AuditEvents.COPY_ATTEMPT);
            return false;
          case "x": // Cut
            e.preventDefault();
            report(AuditEvents.CUT_ATTEMPT);
            return false;
          case "u": // View source
            e.preventDefault();
            report(AuditEvents.VIEW_SOURCE_ATTEMPT);
            return false;
          default:
            break;
        }
      }

      // Screen-capture attempts are checked at the top of the handler
      // (best-effort: OS-level capture cannot be blocked by the browser; we
      // detect and log for auditing).
      return undefined;
    };

    // Windows / Linux: the PrintScreen key typically only fires on keyup in
    // browsers, so it is handled in a dedicated keyup listener below.
    const keyupHandler = (e) => {
      logMetaShift(e, "keyup  ");
      if (isWindows && (e.metaKey || mods.meta || isMetaKeyEvent(e))) {
        winLog(describeKey(e) + " " + describeMods());
      } else if (isMac && (e.metaKey || isMetaKeyEvent(e) || isShiftKeyEvent(e))) {
        winLog(describeKey(e) + " -> capture combo: " + isMacCaptureCombo(e));
      }
      macKeyUp(e);
      const key = (e.key || "").toLowerCase();
      const code = e.keyCode || e.which;
      if (key === "printscreen" || code === 44) {
        reportCaptureOnce("Print Screen key, keyup");
        trackModifiers(e, false);
        return;
      }
      // Second chance for the macOS and Windows combos: the OS consumes the
      // shortcut's keydown, so the page often sees only the keyup.
      // reportCaptureOnce de-duplicates against the keydown. Checked BEFORE
      // the modifier state is updated so a Win key released a moment earlier
      // is still counted.
      checkCaptureCombos(e, "keyup");
      trackModifiers(e, false);
    };

    /**
     * Windows fallback for Win + Shift + S / R.
     *
     * If the letter's keydown AND keyup were both consumed, the only trace
     * the page gets is: Win and Shift went down, no other key arrived, and the
     * window lost focus to the Snipping Tool overlay. That exact sequence is
     * treated as a capture attempt.
     *
     * Win + G (Game Bar) has no equivalent fallback: "Win held, then blur" is
     * also what Win + E / R / V / A / N and friends look like, and treating
     * those as captures would ban readers who did nothing wrong. Game Bar
     * opens slowly, so the G keyup normally reaches the page first and is
     * matched above.
     */
    /** Win + Shift went down, nothing else arrived, and the combo is recent. */
    const winShiftPending = (maxAgeMs) => {
      const now = Date.now();
      return (
        mods.meta &&
        mods.shift &&
        !mods.alt &&
        !mods.otherKey &&
        now - mods.metaAt < maxAgeMs &&
        now - mods.shiftAt < maxAgeMs
      );
    };

    // Window level only: with a capture listener on `window`, blur / focus of
    // any element inside the page would otherwise land here too.
    const isWindowTarget = (e) =>
      !e || e.target === window || e.target === document || e.target == null;

    const onWindowBlur = (e) => {
      if (!isWindowTarget(e)) return;
      if (isWindows) {
        winLog(
          "window blur " + describeMods() +
          (mods.chordReleasedAt
            ? " chordReleased=" + (Date.now() - mods.chordReleasedAt) + "ms ago"
            : "")
        );
        if (winShiftPending(HELD_MAX_MS)) {
          reportCaptureOnce(
            "Win+Shift+S / Win+Shift+R (Snipping Tool), focus moved to capture overlay"
          );
          resetMods();
          return;
        }
        // Win + Shift released with the letter swallowed, and focus left
        // shortly after - the Snipping Tool (recorder) opening.
        if (
          mods.chordReleasedAt &&
          Date.now() - mods.chordReleasedAt < CHORD_BLUR_MS
        ) {
          reportCaptureOnce(
            "Win+Shift+S / Win+Shift+R (Snipping Tool), focus moved after the shortcut was released"
          );
          resetMods();
          return;
        }
        mods.chordReleasedAt = 0;
        // Otherwise the tracked state is KEPT: whether the Win / Shift
        // key-ups come back when the reader returns is itself the signal the
        // "lost key-up" fallback below uses.
        return;
      }
      winLog("window blur visibility=" + document.visibilityState);
      macOnBlur();
      resetMods();
    };

    /**
     * Windows fallback #2 - "lost key-ups".
     *
     * Some Windows builds show the Snipping Tool overlay without the browser
     * window ever firing `blur`. The trace left behind is that Win and Shift
     * went down on this page but their key-ups never came back: they were
     * delivered to the overlay. So when the next pointer / key event (or the
     * window regaining focus) shows Win and Shift no longer held, and no
     * key-up for them was seen, the shortcut was taken by the OS.
     */
    const checkLostKeyups = (source, e) => {
      if (!isWindows) return;
      if (!mods.meta && !mods.shift) return;
      // Still physically held - nothing lost yet.
      if (e && (e.metaKey || e.shiftKey)) return;
      if (winShiftPending(LOST_KEYUP_MAX_MS)) {
        winLog("lost Win+Shift key-ups detected on " + source + " " + describeMods());
        reportCaptureOnce(
          "Win+Shift+S / Win+Shift+R (Snipping Tool), key-ups taken by capture overlay"
        );
      }
      resetMods();
    };

    const onWindowFocus = (e) => {
      if (!isWindowTarget(e)) return;
      if (isWindows) {
        winLog("window focus " + describeMods());
        checkLostKeyups("focus", null);
        return;
      }
      winLog("window focus");
      if (isMac && !(mac.cmd || mac.shift)) macResetChord();
      resetMods();
    };
    // Diagnostics only (macOS): shows whether a capture tool hides the page.
    const onVisibilityDiag = () => {
      winLog("visibility=" + document.visibilityState);
      macOnVisibility();
    };
    const onMacPointer = (e) => macOnPointer(e);
    const onPointer = (e) => {
      // A click on the page after the chord means nothing was opened by it.
      if (e.type === "pointerdown") mods.chordReleasedAt = 0;
      checkLostKeyups(e.type, e);
    };

    /**
     * Keyboard Lock (Chromium: Chrome / Edge / Opera, secure context only).
     *
     * On Windows the OS takes Win + G / Win + Shift + R before the browser
     * ever sees the letter, and a Win + G press is indistinguishable from a
     * plain Win tap (Start menu) from inside the page. While the document is
     * in full screen, navigator.keyboard.lock() makes Chromium capture the
     * Win keys itself, so the OS never sees the shortcut: the key events come
     * to the page as normal keydowns (and are matched above) and the Snipping
     * Tool / Game Bar does not open. Esc is not locked, so leaving full screen
     * works as usual. The lock only takes effect in full screen and is
     * released by the browser on exit; it is re-applied on every entry.
     */
    const keyboardApi =
      isWindows && typeof navigator !== "undefined" ? navigator.keyboard : null;
    const canLock =
      !!keyboardApi &&
      typeof keyboardApi.lock === "function" &&
      typeof keyboardApi.unlock === "function";
    const LOCKED_KEYS = ["MetaLeft", "MetaRight"];
    const isFullscreenNow = () =>
      !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
    const applyKeyboardLock = () => {
      if (!canLock) return;
      if (!isFullscreenNow()) return;
      try {
        const result = keyboardApi.lock(LOCKED_KEYS);
        if (result && typeof result.then === "function") {
          result.then(
            () => winLog("keyboard lock ON (" + LOCKED_KEYS.join(", ") + ")"),
            (err) => winLog("keyboard lock refused: " + (err && err.message))
          );
        }
      } catch (err) {
        winLog("keyboard lock failed: " + (err && err.message));
      }
    };
    const releaseKeyboardLock = () => {
      if (!canLock) return;
      try {
        keyboardApi.unlock();
      } catch (err) {
        /* ignore */
      }
    };
    const onFullscreenChange = () => {
      resetMods();
      if (isFullscreenNow()) applyKeyboardLock();
      else releaseKeyboardLock();
    };
    const fullscreenEvents = ["fullscreenchange", "webkitfullscreenchange"];
    const pointerEvents = ["pointerdown", "pointermove", "wheel"];

    // Also intercept the print dialog directly (Ctrl+P / menu print). Not in
    // capture-only mode: RShield does not govern printing.
    const beforePrint = () => report(AuditEvents.PRINT_ATTEMPT);

    document.addEventListener("keydown", handler, true);
    document.addEventListener("keyup", keyupHandler, true);
    window.addEventListener("blur", onWindowBlur, true);
    window.addEventListener("focus", onWindowFocus, true);
    if (isWindows) {
      pointerEvents.forEach((ev) =>
        document.addEventListener(ev, onPointer, { capture: true, passive: true })
      );
      fullscreenEvents.forEach((ev) =>
        document.addEventListener(ev, onFullscreenChange, false)
      );
      applyKeyboardLock();
    }
    if (isMac) {
      document.addEventListener("visibilitychange", onVisibilityDiag, false);
      ["pointerdown", "pointermove"].forEach((ev) =>
        document.addEventListener(ev, onMacPointer, { capture: true, passive: true })
      );
    }
    if (!captureOnly) window.addEventListener("beforeprint", beforePrint, false);
    winLog(
      "capture detection ARMED (captureOnly=" + captureOnly +
      ", keyboardLock=" + (canLock ? "supported" : "unsupported") +
      ", fullscreen=" + isFullscreenNow() + ")"
    );

    return () => {
      winLog("capture detection disarmed");
      if (isMac) {
        document.removeEventListener("visibilitychange", onVisibilityDiag, false);
        ["pointerdown", "pointermove"].forEach((ev) =>
          document.removeEventListener(ev, onMacPointer, { capture: true, passive: true })
        );
        // The guard can be switched off by the very blur a capture tool causes
        // (e.g. "Resume on full screen" pausing the view). A chord that is
        // still settling is reported now rather than dropped.
        const pending = !!mac.settleTimer && !mac.hiddenDuringSettle;
        macClearSettle();
        if (pending) {
          reportCaptureOnce("macOS Cmd+Shift+3/4/5 (digit taken by the system), guard paused");
        }
      }
      if (isWindows) {
        fullscreenEvents.forEach((ev) =>
          document.removeEventListener(ev, onFullscreenChange, false)
        );
        releaseKeyboardLock();
      }
      document.removeEventListener("keydown", handler, true);
      document.removeEventListener("keyup", keyupHandler, true);
      window.removeEventListener("blur", onWindowBlur, true);
      window.removeEventListener("focus", onWindowFocus, true);
      if (isWindows) {
        pointerEvents.forEach((ev) =>
          document.removeEventListener(ev, onPointer, { capture: true, passive: true })
        );
      }
      if (!captureOnly) window.removeEventListener("beforeprint", beforePrint, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, captureOnly]);
}

export default useKeyboardGuard;
